<template>
  <div class="presetForm">
    <header class="formHeader">
      <div>
        <h2>Itens predefinidos de receita</h2>
        <span>{{ form.id ? "Editando item predefinido" : "Novo item predefinido" }}</span>
      </div>
      <button
        type="button"
        class="closeButton"
        title="Fechar"
        aria-label="Fechar"
        @click="close"
      >
        <b-icon-x-lg />
      </button>
    </header>

    <div class="presetBody">
      <div v-if="loading" class="formState">
        <b-spinner small /> Carregando item...
      </div>

      <div v-else class="formContent">
      <section class="responsibleSection">
        <h3 class="sectionTitle">1 - Responsável Técnico</h3>
        <div class="responsibleField">
          <InputSelect
            label="Responsável Técnico"
            valueField="id"
            textField="nome"
            :items="responsibles"
            :dataInput="form.idResponsavelTecnico"
            :dataSelect.sync="form.idResponsavelTecnico"
            :enableIconCreate="false"
          />
        </div>
      </section>

      <section class="sectionAgro">
        <h3 class="sectionTitle">2 - Produto, Cultura e Diagnóstico</h3>
        <div class="rowContent">
          <InputProducts
            style="width: 50%"
            label="1. Selecione o Agrotóxico (Produto)"
            typeFilter="itens"
            :isEdit="form.infortecnicaReceita.idAgrotoxico"
            :dataInput.sync="form.infortecnicaReceita.idAgrotoxico"
            :onSearch="findPesticides"
            :enableIconCreate="false"
            :clearSelected="clearProductSelection"
            :searchFields="['nomeComum']"
            @selectItem="selectPesticide"
            @closed="showPesticideAlert"
          />
          <InputSelect
            label="2. Selecione a Cultura"
            valueField="idCultura"
            textField="nome"
            :items="cultures"
            :dataInput="form.infortecnicaReceita.idCultura"
            :dataSelect.sync="form.infortecnicaReceita.idCultura"
            :enableIconCreate="false"
            :isDisabled="!cultures.length"
            @selectedCompletedItem="selectCulture"
          />
          <InputSelect
            label="3. Selecione o Diagnóstico (Alvo)"
            valueField="idIndicacao"
            textField="masterName"
            :items="diagnoses"
            :dataInput="form.infortecnicaReceita.idIndicacao"
            :dataSelect.sync="form.infortecnicaReceita.idIndicacao"
            :enableIconCreate="false"
            :isDisabled="!diagnoses.length"
            @selectedCompletedItem="selectDiagnosis"
          />
        </div>

        <div class="rowContent technicalRow">
          <div class="fieldWithHint">
            <Input
              label="Int. de aplicação (dias)"
              labelFixed
              :dataInput.sync="form.infortecnicaReceita.intervaloAplicacao"
            />
            <small>Int. de seg.: {{ form.infortecnicaReceita.intervaloSeguranca }}</small>
          </div>
          <div class="fieldWithHint">
            <Input
              label="N° de Aplicações"
              labelFixed
              :dataInput.sync="form.infortecnicaReceita.numAplicacoes"
            />
            <small>Máx. bula: {{ form.infortecnicaReceita.maxAplicacoesBula }}</small>
          </div>
          <Input
            label="Estágio de cultura"
            labelFixed
            :dataInput.sync="form.infortecnicaReceita.estagioCultura"
            :list="`estagios-cultura-${_uid}`"
          />
          <datalist :id="`estagios-cultura-${_uid}`">
            <option v-for="stage in cultureStages" :key="stage" :value="stage" />
          </datalist>
          <div class="fieldWithHint">
            <Input
              label="Dose utilizada"
              labelFixed
              :dataInput.sync="form.infortecnicaReceita.doseUtilizada"
            />
            <small>
              Dose/Bula: {{ form.infortecnicaReceita.doseBula }} -
              {{ form.infortecnicaReceita.unidadeDose }}
            </small>
          </div>
          <div class="fieldWithHint">
            <Input
              label="Vol. de calda utilizado"
              labelFixed
              :dataInput.sync="form.infortecnicaReceita.volumeCaldaUtilizado"
            />
            <small>
              Vol. Calda/Bula: {{ form.infortecnicaReceita.volumeCaldaBula }} -
              {{ form.infortecnicaReceita.unidadeVolumeCalda }}
            </small>
          </div>
          <div class="doseMode">
            <span>Utilizar Dose / Volume de calda</span>
            <div class="radio-input">
              <label v-for="mode in doseModes" :key="mode.value">
                <input
                  v-model="form.infortecnicaReceita.doseSelectionMode"
                  type="radio"
                  name="preset-dose-mode"
                  :value="mode.value"
                  @change="changeDoseMode"
                />
                <span>{{ mode.text }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="rowContent">
          <PresetTextArea
            label="Forma de aplicação (Observações sobre a aplicação)"
            :value.sync="form.infortecnicaReceita.formaAplicacao"
          />
          <PresetTextArea
            label="Época de aplicação e observações adicionais"
            :value.sync="form.infortecnicaReceita.epocaAplicacao"
          />
        </div>
      </section>

      <section class="sectionAgro">
        <h3 class="sectionTitle">3 - Dados Técnicos</h3>
        <div class="rowContent">
          <Input
            label="Área a tratar"
            labelFixed
            :dataInput.sync="form.infortecnicaReceita.areatratar"
            ref="areatratar"
          />
          <InputSelect
            label="Medida"
            valueField="value"
            textField="text"
            :items="measures"
            :dataInput="form.infortecnicaReceita.medida"
            :dataSelect.sync="form.infortecnicaReceita.medida"
            :enableIconCreate="false"
          />
          <b-button class="calculatorButton" @click="showModalCalcArea">
            <b-icon-calculator />
          </b-button>
          <Input
            label="Qt. Embalagem"
            labelFixed
            type="number"
            :dataInput.sync="form.quantidade"
            ref="quantidade"
          />
          <b-button class="calculatorButton" @click="calcQuantProd">
            <b-icon-calculator />
          </b-button>
          <InputSelectStock
            class="packagingField"
            label="Embalagens"
            valueField="descricao"
            textField="descricao"
            :items="packages"
            :dataInput="form.infortecnicaReceita.embalagem"
            :dataSelect.sync="form.infortecnicaReceita.embalagem"
            :enableIconCreate="false"
            @selectedCompletedItem="selectPackage"
          />
          <div class="lotField">
            <label>Lote</label>
            <b-form-input
              v-model="form.infortecnicaReceita.lote"
              class="borderInput shadow-none"
              list="preset-lot-list"
              autocomplete="off"
            />
            <b-form-datalist
              id="preset-lot-list"
              text-field="lote"
              value-field="lote"
              :options="dataLotes"
            />
          </div>
        </div>

        <div class="rowContent">
          <InputSelect
            label="Modalidade de aplicação"
            valueField="id"
            textField="nome"
            :items="applicationMethods"
            :dataInput="form.infortecnicaReceita.idTipoAplicacao"
            :dataSelect.sync="form.infortecnicaReceita.idTipoAplicacao"
            :enableIconCreate="false"
            @selectedCompletedItem="selectApplicationMethod"
          />
          <ApplicationPeriodInput
            :dataInput.sync="form.infortecnicaReceita.horario"
          />
        </div>

        <div class="rowContent technicalTextAreas">
          <PresetTextArea
            label="Modo de Aplicação"
            :value.sync="form.infortecnicaReceita.modoaplicacao"
          />
          <PresetTextArea label="EPI" :value.sync="form.infortecnicaReceita.epi" />
        </div>
      </section>
      </div>

      <section class="listSection">
      <header class="listHeader">
        <div class="listTitle">
          <strong>Itens cadastrados</strong>
          <span class="itemCount">
            {{ presets.length }} {{ presets.length === 1 ? "item" : "itens" }} nesta página
          </span>
        </div>
        <div class="searchGroup">
          <b-form-input
            v-model="query"
            size="sm"
            placeholder="Pesquisar por produto, cultura ou diagnóstico"
            @keyup.enter="search"
          />
          <b-button size="sm" class="searchButton" :disabled="listLoading" @click="search">
            <b-icon-search />
          </b-button>
        </div>
      </header>

      <div class="tableWrapper">
        <div class="presetTable">
          <div v-if="listLoading" class="listState"><b-spinner small /> Carregando...</div>
          <div v-else-if="!presets.length" class="listState">
            Não há itens predefinidos para exibir.
          </div>
          <template v-else>
            <div
              v-for="(preset, index) in presets"
              :key="preset.id"
              class="tableRow"
            >
              <div class="cell productCell">
                <span class="itemNumber">{{ (page - 1) * limit + index + 1 }}</span>
                <div class="cellContent">
                  <span class="cellLabel">Produto</span>
                  <span
                    class="cellValue"
                    v-b-tooltip.hover.top="preset.infortecnicaReceita.agrotoxico"
                  >
                    {{ preset.infortecnicaReceita.agrotoxico || "--" }}
                  </span>
                </div>
              </div>
              <div class="cell cultureCell">
                <span class="cellLabel">Cultura</span>
                <span class="cellValue">
                  {{ preset.infortecnicaReceita.nomeCultura || "--" }}
                </span>
              </div>
              <div class="cell diagnosisCell">
                <span class="cellLabel">Diagnóstico</span>
                <span
                  class="cellValue"
                  v-b-tooltip.hover.top="preset.infortecnicaReceita.nomeProblema"
                >
                  {{ preset.infortecnicaReceita.nomeProblema || "--" }}
                </span>
              </div>
              <div class="cell responsibleCell">
                <span class="cellLabel">Responsável</span>
                <span class="cellValue" v-b-tooltip.hover.top="responsibleName(preset)">
                  {{ responsibleName(preset) }}
                </span>
              </div>
              <div class="cell quantityCell">
                <span class="cellLabel">Quantidade</span>
                <span class="quantityValue">{{ preset.quantidade }}</span>
              </div>
              <div class="cell packageCell">
                <span class="cellLabel">Embalagem</span>
                <span
                  class="cellValue"
                  v-b-tooltip.hover.top="preset.infortecnicaReceita.embalagem"
                >
                  {{ preset.infortecnicaReceita.embalagem || "--" }}
                </span>
              </div>
              <div class="actionsCell">
                <button
                  type="button"
                  class="actionButton editButton"
                  title="Editar"
                  v-b-tooltip.hover.top="'Editar item predefinido'"
                  @click="editPreset(preset.id)"
                >
                  <b-icon-pencil-fill />
                </button>
                <button
                  type="button"
                  class="actionButton deleteButton"
                  title="Excluir"
                  v-b-tooltip.hover.top="'Excluir item predefinido'"
                  @click="confirmDelete(preset.id)"
                >
                  <b-icon-trash-fill />
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="presetPagination">
        <button class="pageButton" :disabled="page <= 1 || listLoading" @click="changePage(page - 1)">
          <b-icon-chevron-left />
        </button>
        <span class="currentPage">{{ page }}</span>
        <button
          class="pageButton"
          :disabled="!hasNextPage || listLoading"
          @click="changePage(page + 1)"
        >
          <b-icon-chevron-right />
        </button>
      </div>
      </section>
    </div>

    <footer class="formActions">
      <b-button class="secondaryButton" :disabled="saving" @click="resetForm">
        Cancelar
      </b-button>
      <b-button class="primaryButton" :disabled="saving || loading" @click="save">
        <b-spinner v-if="saving" small class="mr-2" />
        {{ form.id ? "Atualizar" : "Salvar" }}
      </b-button>
    </footer>

    <ModalConfirm
      typeModal="success"
      modalName="modal-preset-calc-area"
      title="Calculadora"
      subtitle="Calcular a área tratada."
      headerIcon="b-icon-calculator-fill"
      leftButtonText="Fechar"
      rightButtonText="Calcular"
      :rightButtonClick="calcArea"
      sizeModal="sm"
    >
      <div class="calculatorFields">
        <div class="calculatorRow">
          <Input
            label="Quantidade do produto"
            labelFixed
            :dataInput.sync="calcAreaItem.quantidade"
          />
          <Input
            label="Nº Aplicações"
            labelFixed
            :dataInput.sync="calcAreaItem.numeroAplicacoes"
          />
        </div>
        <div class="calculatorRow">
          <Input label="Dose" labelFixed :dataInput.sync="calcAreaItem.dose" />
          <Input
            label="Volume de calda"
            labelFixed
            :dataInput.sync="calcAreaItem.volumeCalda"
            isDisabled
          />
        </div>
      </div>
    </ModalConfirm>

    <ModalConfirm
      modalName="modal-confirm-delete-prescription-preset"
      title="Excluir item predefinido"
      subtitle="Esta ação não pode ser desfeita. Deseja continuar ?"
      :rightButtonClick="remove"
      @modalClosed="presetToDelete = null"
    />

    <ModalConfirm
      typeModal="warning"
      modalName="modal-preset-alert"
      :title="alertTitle"
      :subtitle="alertMessage"
      leftButtonText="Fechar"
      :showRightButton="false"
      headerIcon="b-icon-exclamation-triangle-fill"
    />

    <ModalConfirm
      typeModal="warning"
      modalName="modal-confirm-save-prescription-preset"
      title="Confirmar item predefinido"
      :subtitle="saveAlertMessage"
      leftButtonText="Revisar"
      rightButtonText="Salvar mesmo assim"
      :rightButtonClick="persistPreset"
      headerIcon="b-icon-exclamation-triangle-fill"
    />
  </div>
</template>

<script>
import Input from "../UI/CustomInputs/Input.vue";
import ApplicationPeriodInput from "./ApplicationPeriodInput.vue";
import InputProducts from "../UI/CustomInputs/InputProducts.vue";
import InputSelect from "../UI/CustomInputs/InputSelect.vue";
import InputSelectStock from "../UI/CustomInputs/InputSelectStock.vue";
import ModalConfirm from "../Modals/ModalConfirm.vue";
import PresetTextArea from "./PresetTextArea.vue";
import ServiceCompendio from "../../services/serviceCompendio";
import servicePrescriptionPreset from "../../services/servicePrescriptionPreset";
import serviceProducts from "../../services/serviceProducts";
import serviceResponsible from "../../services/serviceResponsible";
import { calculateProductQuantity } from "../../utils/calculateProductQuantity";
import { cultureStages } from "../../utils/cultureStages";

const emptyTechnicalInfo = () => ({
  registroAgrotox: "",
  idAgrotoxico: "",
  lote: "",
  agrotoxico: "",
  idEmbalagem: "",
  embalagem: "",
  idCultura: "",
  cultura: {},
  nomeCultura: "",
  idProblema: "",
  idIndicacao: "",
  nomeProblema: "",
  nomeProblemaCientifico: "",
  problema: {},
  doseBula: "",
  doseUtilizada: "",
  unidadeDose: "",
  volumeCaldaBula: "",
  volumeCaldaUtilizado: "",
  unidadeVolumeCalda: "",
  intervaloAplicacao: 0,
  intervaloSeguranca: "",
  maxAplicacoesBula: 0,
  numAplicacoes: 1,
  estagioCultura: "",
  formaAplicacao: "",
  epocaAplicacao: "",
  doseSelectionMode: "intervalo",
  volumeCaldaSelectionMode: "intervalo",
  areatratar: 1,
  medida: "ha",
  modaplicacao: "",
  modoaplicacao: "",
  idTipoAplicacao: null,
  nomeTipoAplicacao: "",
  tipoAplicacao: {},
  tipoAplicacaoCodEs: "",
  tipoAplicacaoCodMt: "",
  horario: "",
  epi: "",
});

const emptyForm = () => ({
  id: null,
  idResponsavelTecnico: "",
  quantidade: 1,
  infortecnicaReceita: emptyTechnicalInfo(),
});

export default {
  name: "PrescriptionPresetForm",
  components: {
    Input,
    ApplicationPeriodInput,
    InputProducts,
    InputSelect,
    InputSelectStock,
    ModalConfirm,
    PresetTextArea,
  },
  props: {
    presetId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      cultureStages,
      form: emptyForm(),
      responsibles: [],
      cultures: [],
      diagnoses: [],
      packages: [],
      dataLotes: [],
      currentProductStock: null,
      applicationMethods: [],
      selectedCulture: null,
      selectedPesticideId: null,
      descriptions: { pesticide: "", application: "", culture: "", diagnosis: "" },
      clearProductSelection: false,
      loading: false,
      saving: false,
      listLoading: false,
      presets: [],
      query: "",
      page: 1,
      limit: 10,
      hasNextPage: false,
      presetToDelete: null,
      alertTitle: "Aviso",
      alertMessage: "",
      saveAlertMessage: "",
      calcAreaItem: {
        quantidade: null,
        numeroAplicacoes: 1,
        dose: null,
        volumeCalda: null,
      },
      measures: [
        { value: "ha", text: "Ha" },
        { value: "ton", text: "Ton" },
        { value: "m3", text: "M³" },
        { value: "m2", text: "M²" },
      ],
      doseModes: [
        { value: "minimo", text: "Mínimo" },
        { value: "maximo", text: "Máximo" },
        { value: "intervalo", text: "Intervalo" },
      ],
    };
  },
  async mounted() {
    this.loading = true;
    await Promise.all([
      this.presetId ? this.loadPreset(this.presetId) : this.loadResponsibles(),
      this.loadPresets(),
    ]);
    this.loading = false;
  },
  methods: {
    close() {
      this.$emit("close");
    },
    normalizeList(result) {
      if (Array.isArray(result)) return result;
      if (Array.isArray(result?.data)) return result.data;
      if (Array.isArray(result?.items)) return result.items;
      if (Array.isArray(result?.rows)) return result.rows;
      return [];
    },
    responsibleName(preset) {
      const responsible = this.responsibles.find(
        (item) => String(item.id) === String(preset.idResponsavelTecnico)
      );
      return (
        preset.nomeResponsavelTecnico ||
        preset.nomeResponsavel ||
        preset.nomeTecnico ||
        preset.responsavelTecnico?.nome ||
        responsible?.nome ||
        "--"
      );
    },
    search() {
      this.page = 1;
      this.loadPresets();
    },
    changePage(page) {
      this.page = page;
      this.loadPresets();
    },
    async loadPresets() {
      try {
        this.listLoading = true;
        const result = await servicePrescriptionPreset.findAll(
          this.query,
          this.page,
          this.limit
        );
        this.presets = this.normalizeList(result).map((item) => ({
          ...item,
          infortecnicaReceita: item.infortecnicaReceita || {},
        }));
        const total = Number(result?.total || result?.count || 0);
        this.hasNextPage = total
          ? this.page * this.limit < total
          : this.presets.length === this.limit;
      } catch (error) {
        this.presets = [];
        this.$toast.error("Não foi possível carregar os itens predefinidos.");
      } finally {
        this.listLoading = false;
      }
    },
    async editPreset(id) {
      try {
        this.loading = true;
        await this.loadPreset(id);
      } finally {
        this.loading = false;
      }
    },
    confirmDelete(id) {
      this.presetToDelete = id;
      this.$bvModal.show("modal-confirm-delete-prescription-preset");
    },
    async remove() {
      if (!this.presetToDelete) return;
      try {
        const deletedId = this.presetToDelete;
        await servicePrescriptionPreset.delete(deletedId);
        if (String(this.form.id) === String(deletedId)) await this.resetForm();
        await this.loadPresets();
        if (!this.presets.length && this.page > 1) {
          this.page -= 1;
          await this.loadPresets();
        }
        this.$toast.success("Item predefinido excluído.");
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message || "Não foi possível excluir o item predefinido."
        );
      }
    },
    async loadResponsibles() {
      try {
        this.responsibles = await serviceResponsible.getResponsibleForSelectBox(
          this.form.idResponsavelTecnico
        );
      } catch (error) {
        this.$toast.error("Falha ao carregar responsáveis técnicos.");
      }
    },
    async resetForm() {
      this.form = emptyForm();
      this.calcAreaItem = {
        quantidade: null,
        numeroAplicacoes: 1,
        dose: null,
        volumeCalda: null,
      };
      this.cultures = [];
      this.diagnoses = [];
      this.packages = [];
      this.dataLotes = [];
      this.currentProductStock = null;
      this.applicationMethods = [];
      this.selectedCulture = null;
      this.selectedPesticideId = null;
      this.descriptions = { pesticide: "", application: "", culture: "", diagnosis: "" };
      this.clearProductSelection = !this.clearProductSelection;
      await this.loadResponsibles();
    },
    async findPesticides(id, q, filterType, page) {
      try {
        const { data } = await ServiceCompendio.combobox(id, q, filterType, page);
        return (data || []).map((item) => ({
          id: item.id,
          nome: item.nomeComum,
          registro: item.registro,
          aplicacao: item.aplicacao,
          classificacao: item.classificacao,
          mensagemAlerta: item.mensagemAlerta,
        }));
      } catch (error) {
        this.$toast.error("Falha ao carregar produtos (agrotóxicos).");
        return [];
      }
    },
    async selectPesticide(pesticide) {
      const previousId = this.selectedPesticideId;
      if (!pesticide?.id) {
        if (previousId) {
          this.clearPesticideDependencies();
          this.form.infortecnicaReceita = emptyTechnicalInfo();
        }
        this.selectedPesticideId = null;
        return;
      }
      const changed = String(previousId || "") !== String(pesticide.id);
      if (changed) {
        this.clearPesticideDependencies();
        this.form.infortecnicaReceita = {
          ...emptyTechnicalInfo(),
          idAgrotoxico: pesticide.id,
          registroAgrotox: pesticide.registro,
          agrotoxico: pesticide.nome,
          classificacao: pesticide.classificacao,
          mensagemAlerta: pesticide.mensagemAlerta,
          agrotoxicoSnapshot: { ...pesticide },
        };
      } else {
        Object.assign(this.form.infortecnicaReceita, {
          idAgrotoxico: pesticide.id,
          registroAgrotox: pesticide.registro,
          agrotoxico: pesticide.nome,
          classificacao: pesticide.classificacao,
          mensagemAlerta: pesticide.mensagemAlerta,
          agrotoxicoSnapshot: { ...pesticide },
        });
      }
      this.selectedPesticideId = pesticide.id;
      this.descriptions.pesticide = this.normalizeDescription(pesticide.aplicacao);
      if (!this.form.infortecnicaReceita.epi) {
        this.form.infortecnicaReceita.epi = this.defaultEpi(pesticide.classificacao);
      }
      await Promise.all([
        this.loadCultures(pesticide.id),
        this.loadPackages(pesticide.id, pesticide.registro),
        this.loadApplicationMethods(pesticide.id),
      ]);
      this.recalculateApplicationMode();
    },
    clearPesticideDependencies() {
      this.cultures = [];
      this.diagnoses = [];
      this.packages = [];
      this.dataLotes = [];
      this.applicationMethods = [];
      this.selectedCulture = null;
      this.currentProductStock = null;
      this.descriptions = {
        pesticide: "",
        application: "",
        culture: "",
        diagnosis: "",
      };
    },
    showAlert(title, message) {
      if (!message) return;
      this.alertTitle = title;
      this.alertMessage = message;
      this.$bvModal.show("modal-preset-alert");
    },
    showPesticideAlert() {
      this.showAlert(
        "Atenção ao agrotóxico",
        this.form.infortecnicaReceita.mensagemAlerta
      );
    },
    async loadCultures(pesticideId) {
      try {
        const result = await ServiceCompendio.findApplicationsByCrop(pesticideId);
        this.cultures = Array.isArray(result)
          ? result
              .map((item) => ({ ...item, nome: item.cultura?.nome || item.nomeCultura }))
              .sort((a, b) => a.nome.localeCompare(b.nome))
          : [];
      } catch (error) {
        this.cultures = [];
        this.$toast.error("Erro ao buscar culturas.");
      }
    },
    async selectCulture(culture) {
      if (!culture?.idCultura) return;
      const previousCultureId = this.selectedCulture?.idCultura;
      if (String(previousCultureId || "") !== String(culture.idCultura)) {
        Object.assign(this.form.infortecnicaReceita, {
          idProblema: "",
          idIndicacao: "",
          nomeProblema: "",
          nomeProblemaCientifico: "",
          problema: {},
        });
        this.diagnoses = [];
        this.descriptions.diagnosis = "";
      }
      this.selectedCulture = culture;
      Object.assign(this.form.infortecnicaReceita, {
        idCultura: culture.idCultura,
        cultura: culture,
        nomeCultura: culture.nomeCultura || culture.nome,
        maxAplicacoesBula: culture.numeroAplicacao,
      });
      this.descriptions.culture = this.normalizeDescription(culture.descricaoAplicacao);
      await this.loadDiagnoses(culture.idCultura);
      this.recalculateApplicationMode();
    },
    async loadDiagnoses(cultureId) {
      try {
        const result = await ServiceCompendio.findDiagnosysIdCulture(
          this.form.infortecnicaReceita.idAgrotoxico,
          cultureId
        );
        this.diagnoses = Array.isArray(result)
          ? result.map((item) => ({
              ...item,
              masterName: `${item.nomeComum} (${item.nomeCientifico})`,
            }))
          : [];
      } catch (error) {
        this.diagnoses = [];
        this.$toast.error("Erro ao buscar diagnósticos.");
      }
    },
    selectDiagnosis(diagnosis) {
      if (!diagnosis) return;
      const culture = this.selectedCulture || this.form.infortecnicaReceita.cultura || {};
      const info = this.form.infortecnicaReceita;
      Object.assign(info, {
        idIndicacao: diagnosis.idIndicacao,
        idProblema: diagnosis.idProblema,
        nomeProblema: diagnosis.nomeComum,
        nomeProblemaCientifico: diagnosis.nomeProblemaCientifico || diagnosis.nomeCientifico,
        problema: diagnosis,
        doseBula: `${diagnosis.doseMinima} - ${diagnosis.doseMaxima}`,
        unidadeDose: diagnosis.unidadeDose,
        volumeCaldaBula: `${diagnosis.doseMinimaVolumeCalda} - ${diagnosis.doseMaximaVolumeCalda}`,
        unidadeVolumeCalda: diagnosis.unidadeVolumeCalda,
        intervaloAplicacao: culture.intervaloEntreAplicacoes,
        intervaloSeguranca: culture.intervaloSeguranca,
        numAplicacoes: 1,
        formaAplicacao: diagnosis.observacao,
        doseSelectionMode: "intervalo",
        volumeCaldaSelectionMode: "intervalo",
      });
      this.descriptions.diagnosis = this.normalizeDescription(diagnosis.observacao);
      this.changeDoseMode();
      this.recalculateApplicationMode();
    },
    async loadPackages(pesticideId, register) {
      try {
        const [packages, products] = await Promise.all([
          ServiceCompendio.findAllEmbalagensByIdAgrotoxico(pesticideId),
          serviceProducts.findPerRegister(register),
        ]);
        const inStock = new Set(
          (products || [])
            .filter((product) => Number(product.estoque) > 0)
            .map((product) => product.embalagem)
        );
        this.packages = (packages || []).map((item) => ({
          ...item,
          inStock: inStock.has(item.descricao),
        }));
        if (!this.packages.length) {
          this.showAlert(
            "Embalagem não encontrada",
            "O Compêndio não possui embalagem cadastrada para este agrotóxico."
          );
        }
      } catch (error) {
        this.packages = [];
        this.$toast.error("Erro ao buscar embalagens.");
      }
    },
    async selectPackage(packageItem) {
      if (!packageItem) return;
      this.form.infortecnicaReceita.idEmbalagem = packageItem.id;
      this.form.infortecnicaReceita.embalagem = packageItem.descricao;
      this.form.infortecnicaReceita.embalagemSnapshot = { ...packageItem };
      await this.loadLots(!this.form.id || !this.form.infortecnicaReceita.lote);
    },
    async loadLots(selectFirst = false) {
      const info = this.form.infortecnicaReceita;
      try {
        const result = await serviceProducts.findLotes(
          info.idAgrotoxico,
          info.embalagem,
          info.registroAgrotox
        );
        this.dataLotes = result?.lotes || [];
        this.currentProductStock = (result?.infoProd || []).reduce(
          (total, product) => total + Number(product.estoque || 0),
          0
        );
        if (selectFirst) {
          info.lote = this.dataLotes[0]?.lote || "";
        } else if (
          info.lote &&
          !this.dataLotes.some((lot) => String(lot.lote) === String(info.lote))
        ) {
          info.lote = "";
        }
      } catch (error) {
        this.dataLotes = [];
        this.currentProductStock = null;
        this.$toast.error("Erro ao buscar lotes da embalagem.");
      }
    },
    async loadApplicationMethods(pesticideId) {
      try {
        const result = await ServiceCompendio.findApplicationMethods(pesticideId);
        this.applicationMethods = (result?.aplicacoes || []).map((item) => ({
          id: item.tiposAplicacoes.id,
          nome: item.tiposAplicacoes.nome,
          descricao: item.descricao,
          codEs: item.tiposAplicacoes.codEs,
          codMt: item.tiposAplicacoes.codMt,
          mensagemAlerta: item.mensagemAlerta,
        }));
      } catch (error) {
        this.applicationMethods = [];
        this.$toast.error("Erro ao buscar modalidades de aplicação.");
      }
    },
    selectApplicationMethod(method) {
      if (!method) return;
      const info = this.form.infortecnicaReceita;
      this.descriptions.application = this.normalizeDescription(method.descricao);
      info.nomeTipoAplicacao = method.nome;
      info.tipoAplicacaoCodEs = method.codEs;
      info.tipoAplicacaoCodMt = method.codMt;
      info.tipoAplicacao = {
        ...method,
      };
      this.recalculateApplicationMode();
      this.showAlert("Atenção à modalidade", method.mensagemAlerta);
    },
    recalculateApplicationMode() {
      this.form.infortecnicaReceita.modoaplicacao = [
        this.descriptions.pesticide,
        this.descriptions.application,
        this.descriptions.culture,
        this.descriptions.diagnosis,
      ]
        .filter(Boolean)
        .join("");
    },
    normalizeDescription(value) {
      return value ? value.replace(/\r\n/g, "\n").replace(/\r/g, "\n") : "";
    },
    changeDoseMode() {
      const info = this.form.infortecnicaReceita;
      const selectRangeValue = (range) => {
        const values = String(range || "")
          .split("-")
          .map((item) => item.trim());
        if (info.doseSelectionMode === "minimo") return values[0] || "";
        if (info.doseSelectionMode === "maximo") return values[1] || values[0] || "";
        return range;
      };
      info.doseUtilizada = selectRangeValue(info.doseBula);
      info.volumeCaldaUtilizado = selectRangeValue(info.volumeCaldaBula);
    },
    getDoseMeasure() {
      try {
        const measure = this.form.infortecnicaReceita.unidadeDose.split("/")[1];
        return measure.startsWith("ha") ? "ha" : measure.substring(0, 5);
      } catch (error) {
        this.$toast.info(
          "A calculadora não cobre a unidade de medida deste defensivo."
        );
        return null;
      }
    },
    canCalculate() {
      if (this.form.infortecnicaReceita.doseSelectionMode === "intervalo") {
        this.$toast.info(
          "Selecione a dose mínima ou máxima para utilizar a calculadora."
        );
        return false;
      }

      const supportedUnits = ["L/ha", "L de água/ha", "L de calda/ha"];
      if (
        !supportedUnits.includes(
          this.form.infortecnicaReceita.unidadeVolumeCalda
        )
      ) {
        this.$toast.info(
          "Unidade não suportada pela calculadora. Proceda ao cálculo manual!"
        );
        return false;
      }

      return true;
    },
    showModalCalcArea() {
      if (!this.canCalculate()) return;

      const selectedPackage = this.packages.find(
        (item) => item.descricao === this.form.infortecnicaReceita.embalagem
      );
      const productQuantity = calculateProductQuantity({
        packageQuantity: this.form.quantidade,
        packageCapacity: selectedPackage?.capacidade,
        packageUnit: selectedPackage?.unidade,
        doseUnit: this.form.infortecnicaReceita.unidadeDose,
      });

      if (!productQuantity) {
        return this.$toast.info(
          "Selecione uma embalagem válida para calcular a área tratada."
        );
      }

      this.calcAreaItem = {
        quantidade: productQuantity,
        numeroAplicacoes: this.form.infortecnicaReceita.numAplicacoes,
        dose: this.form.infortecnicaReceita.doseUtilizada,
        volumeCalda: this.form.infortecnicaReceita.volumeCaldaUtilizado,
      };
      this.$bvModal.show("modal-preset-calc-area");
    },
    calcArea() {
      const measure = this.getDoseMeasure();
      if (!measure || !this.canCalculate()) return;

      const quantity = Number(this.calcAreaItem.quantidade);
      const dose = Number(this.calcAreaItem.dose);
      const applications = Number(this.calcAreaItem.numeroAplicacoes);
      const volume = Number(this.calcAreaItem.volumeCalda);

      if (
        !Number.isFinite(quantity) ||
        !Number.isFinite(dose) ||
        !Number.isFinite(applications) ||
        quantity <= 0 ||
        dose <= 0 ||
        applications <= 0
      ) {
        return this.$toast.info("Informe valores válidos para realizar o cálculo.");
      }

      let area;
      if (measure === "ha") {
        area = quantity / dose / applications;
      } else if (measure === "100 L" && Number.isFinite(volume) && volume > 0) {
        area = (quantity / dose) * 100 / volume / applications;
      } else {
        return this.$toast.info(
          "Não foi possível calcular a área com os valores informados."
        );
      }

      this.form.infortecnicaReceita.areatratar = Number(area.toFixed(3));
      this.$bvModal.hide("modal-preset-calc-area");
    },
    calcQuantProd() {
      const measure = this.getDoseMeasure();
      if (!measure || !this.canCalculate()) return;

      const info = this.form.infortecnicaReceita;
      const dose = Number(info.doseUtilizada);
      const area = Number(info.areatratar);
      const applications = Number(info.numAplicacoes);
      const volume = Number(info.volumeCaldaUtilizado);

      if (
        !Number.isFinite(dose) ||
        !Number.isFinite(area) ||
        !Number.isFinite(applications) ||
        dose <= 0 ||
        area <= 0 ||
        applications <= 0
      ) {
        return this.$toast.info("Informe valores válidos para realizar o cálculo.");
      }

      if (measure === "ha") {
        this.form.quantidade = dose * area * applications;
      } else if (measure === "100 L" && Number.isFinite(volume) && volume > 0) {
        this.form.quantidade = dose * area * 0.01 * volume * applications;
      } else {
        return this.$toast.info(
          "Não foi possível calcular a quantidade com os valores informados."
        );
      }
    },
    defaultEpi(classification) {
      if (["I", "II"].includes(classification)) {
        return "LUVAS IMPERMEÁVEIS DE BORRACHA NITRÍLICA OU NEOPRENE. RESPIRADORES SEMIFACIAIS OU FACIAIS COM FILTROS P2 OU P3. ÓCULOS DE SEGURANÇA OU VISEIRA FACIAL. MACACÃO DE ALGODÃO HIDRO-REPELENTE COM MANGAS COMPRIDAS PASSANDO POR CIMA DO PUNHO DAS LUVAS E AS PERNAS DA CALÇAS POR CIMA DAS BOTAS. BONÉ ÁRABE HIDRO-REPELENTE DE ALGODÃO. AVENTAL DE BAGUM E/OU TECIDO EMBORRACHADO ALUMINIZADO. BOTA IMPERMEÁVEL DE CANO ALTO (PVC). AO SURGIMENTO DE QUALQUER DÚVIDA, LEIA AS RECOMENDAÇÕES DO RÓTULO E BULA.";
      }
      if (["III", "IV"].includes(classification)) {
        return "LUVAS IMPERMEÁVEIS DE BORRACHA NITRÍLICA OU NEOPRENE. RESPIRADORES SEMIFACIAIS OU FACIAIS COM FILTROS P2 OU P3. ÓCULOS DE SEGURANÇA OU VISEIRA FACIAL. MACACÃO DE ALGODÃO HIDRO-REPELENTE E IMPERMEÁVEL. BONÉ ÁRABE HIDRO-REPELENTE DE ALGODÃO. BOTA IMPERMEÁVEL DE CANO ALTO (PVC). AO SURGIMENTO DE QUALQUER DÚVIDA, LEIA AS RECOMENDAÇÕES DO RÓTULO E BULA.";
      }
      return "RESPIRADORES SEMIFACIAIS OU FACIAIS COM FILTROS P2 OU P3. ÓCULOS DE SEGURANÇA OU VISEIRA FACIAL. MACACÃO DE ALGODÃO HIDRO-REPELENTE E IMPERMEÁVEL. AVENTAL DE BAGUM E/OU TECIDO EMBORRACHADO ALUMINIZADO. BOTA IMPERMEÁVEL DE CANO ALTO (PVC). AO SURGIMENTO DE QUALQUER DÚVIDA, LEIA AS RECOMENDAÇÕES DO RÓTULO E BULA.";
    },
    buildPayload() {
      return {
        idResponsavelTecnico: this.form.idResponsavelTecnico,
        quantidade: Number(this.form.quantidade),
        infortecnicaReceita: JSON.parse(JSON.stringify(this.form.infortecnicaReceita)),
      };
    },
    save() {
      const info = this.form.infortecnicaReceita;
      const validations = [
        [this.form.idResponsavelTecnico, "Selecione o responsável técnico."],
        [
          info.idAgrotoxico && info.agrotoxico && info.registroAgrotox,
          "Selecione um agrotóxico válido.",
        ],
        [info.idCultura, "Selecione a cultura."],
        [info.idProblema && info.idIndicacao, "Selecione o diagnóstico."],
        [info.idEmbalagem && info.embalagem, "Selecione a embalagem."],
        [
          info.idTipoAplicacao && info.nomeTipoAplicacao,
          "Selecione a modalidade de aplicação.",
        ],
        [Number(this.form.quantidade) > 0, "Informe uma quantidade maior que zero."],
        [Number(info.areatratar) > 0, "Informe uma área maior que zero."],
        [
          Number.isInteger(Number(info.numAplicacoes)) &&
            Number(info.numAplicacoes) > 0,
          "Informe um número inteiro de aplicações maior que zero.",
        ],
      ];
      const invalid = validations.find(([valid]) => !valid);
      if (invalid) return this.$toast.info(invalid[1]);

      const alerts = [
        info.mensagemAlerta,
        info.tipoAplicacao?.mensagemAlerta,
      ].filter(Boolean);
      if (alerts.length) {
        this.saveAlertMessage = alerts.join(" ");
        this.$bvModal.show("modal-confirm-save-prescription-preset");
        return;
      }
      this.persistPreset();
    },
    async persistPreset() {
      if (this.saving) return;
      try {
        this.saving = true;
        const isEditing = Boolean(this.form.id);
        const payload = this.buildPayload();
        if (isEditing) {
          await servicePrescriptionPreset.update(this.form.id, payload);
        } else {
          await servicePrescriptionPreset.save(payload);
        }
        this.$toast.success(
          isEditing ? "Item predefinido atualizado." : "Item predefinido cadastrado."
        );
        await this.loadPresets();
        await this.resetForm();
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message || "Não foi possível salvar o item predefinido."
        );
      } finally {
        this.saving = false;
      }
    },
    ensureOption(items, option, key) {
      if (!option?.[key]) return items;
      const exists = items.some((item) => String(item[key]) === String(option[key]));
      return exists ? items : [option, ...items];
    },
    async loadPreset(id = this.presetId) {
      try {
        const preset = await servicePrescriptionPreset.findById(id);
        const info = preset.infortecnicaReceita || {};
        this.form = {
          ...emptyForm(),
          id: preset.id,
          idResponsavelTecnico: preset.idResponsavelTecnico,
          quantidade: preset.quantidade,
          infortecnicaReceita: {
            ...emptyTechnicalInfo(),
            ...info,
            epi: info.epi || preset.epi || "",
          },
        };
        const loadedInfo = this.form.infortecnicaReceita;
        const cultureSnapshot = loadedInfo.idCultura
          ? {
              ...(loadedInfo.cultura || {}),
              idCultura: loadedInfo.idCultura,
              nome: loadedInfo.nomeCultura,
              nomeCultura: loadedInfo.nomeCultura,
            }
          : null;
        const diagnosisSnapshot = loadedInfo.idIndicacao
          ? {
              ...(loadedInfo.problema || {}),
              idIndicacao: loadedInfo.idIndicacao,
              idProblema: loadedInfo.idProblema,
              nomeComum: loadedInfo.nomeProblema,
              nomeCientifico: loadedInfo.nomeProblemaCientifico,
              masterName: [loadedInfo.nomeProblema, loadedInfo.nomeProblemaCientifico]
                .filter(Boolean)
                .join(" - "),
            }
          : null;
        const packageSnapshot = loadedInfo.embalagem
          ? { id: loadedInfo.idEmbalagem, descricao: loadedInfo.embalagem }
          : null;
        const applicationSnapshot = loadedInfo.idTipoAplicacao
          ? {
              ...(loadedInfo.tipoAplicacao || {}),
              id: loadedInfo.idTipoAplicacao,
              nome: loadedInfo.nomeTipoAplicacao,
            }
          : null;

        await Promise.all([
          this.loadResponsibles(),
          loadedInfo.idAgrotoxico ? this.loadCultures(loadedInfo.idAgrotoxico) : null,
          loadedInfo.idAgrotoxico
            ? this.loadPackages(
                loadedInfo.idAgrotoxico,
                loadedInfo.registroAgrotox
              )
            : null,
          loadedInfo.idAgrotoxico
            ? this.loadApplicationMethods(loadedInfo.idAgrotoxico)
            : null,
        ]);
        if (loadedInfo.idAgrotoxico && loadedInfo.idCultura) {
          await this.loadDiagnoses(loadedInfo.idCultura);
        }

        this.cultures = this.ensureOption(this.cultures, cultureSnapshot, "idCultura");
        this.diagnoses = this.ensureOption(
          this.diagnoses,
          diagnosisSnapshot,
          "idIndicacao"
        );
        this.packages = this.ensureOption(
          this.packages,
          packageSnapshot,
          "descricao"
        );
        this.applicationMethods = this.ensureOption(
          this.applicationMethods,
          applicationSnapshot,
          "id"
        );
        this.selectedCulture =
          this.cultures.find(
            (culture) => String(culture.idCultura) === String(loadedInfo.idCultura)
          ) || cultureSnapshot;
        this.selectedPesticideId = loadedInfo.idAgrotoxico || null;
        this.descriptions = {
          pesticide: this.normalizeDescription(
            loadedInfo.agrotoxicoSnapshot?.aplicacao
          ),
          application: this.normalizeDescription(
            loadedInfo.tipoAplicacao?.descricao
          ),
          culture: this.normalizeDescription(
            this.selectedCulture?.descricaoAplicacao
          ),
          diagnosis: this.normalizeDescription(
            this.diagnoses.find(
              (item) =>
                String(item.idIndicacao) === String(loadedInfo.idIndicacao)
            )?.observacao
          ),
        };
        if (loadedInfo.embalagem) await this.loadLots(false);

        const responsibleSnapshot = preset.responsavelTecnico;
        if (responsibleSnapshot?.id) {
          this.responsibles = this.ensureOption(
            this.responsibles,
            responsibleSnapshot,
            "id"
          );
        }
      } catch (error) {
        this.$toast.error("Não foi possível carregar o item predefinido.");
        await this.resetForm();
      }
    },
  },
};
</script>

<style scoped>
.presetForm {
  background: white;
  border: 1px solid #e3e0e0;
  border-radius: 7px 7px 0 0;
  color: #3a4750;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.formHeader {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: space-between;
  min-height: 48px;
  padding: 8px 20px;
}

.formHeader h2 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.formHeader span {
  color: #7a7a7a;
  font-size: 10px;
}

.closeButton {
  align-items: center;
  align-self: center;
  background: #f2f6f5;
  border: 1px solid #d6e2df;
  border-radius: 6px;
  color: #52615e;
  cursor: pointer;
  display: flex;
  flex: 0 0 32px;
  height: 32px;
  justify-content: center;
  padding: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease, transform 0.15s ease;
  width: 32px;
}

.closeButton:hover,
.closeButton:focus {
  background: #faeaea;
  border-color: #e5baba;
  color: #a72828;
  outline: none;
}

.closeButton:focus-visible {
  box-shadow: 0 0 0 3px rgba(167, 40, 40, 0.15);
}

.closeButton:active {
  transform: scale(0.94);
}

.presetBody {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 72px;
}

.formContent {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 8px;
  padding: 0 20px 10px;
}

.formState {
  align-items: center;
  display: flex;
  flex: 0 0 100px;
  gap: 8px;
  justify-content: center;
}

.sectionAgro {
  background: #fbfdfc;
  border: 1px solid #c8dfd5;
  border-radius: 5px;
  flex: 0 0 auto;
  padding: 10px 12px;
}

.responsibleSection {
  align-items: center;
  display: flex;
  gap: 18px;
  min-height: 52px;
  padding: 4px 12px 8px;
}

.responsibleField {
  width: 45%;
}

.sectionTitle {
  color: #006458;
  font-size: 12px;
  font-weight: 600;
  margin: 0 0 4px;
}

.responsibleSection .sectionTitle {
  flex: 0 0 210px;
  margin: 0;
}

.rowContent {
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 7px 0 3px;
  width: 100%;
}

.rowContent > * {
  flex: 1;
}

.technicalRow {
  align-items: flex-start;
}

.calculatorButton {
  align-items: center;
  background: #006256;
  border-color: #006256;
  display: flex;
  flex: 0 0 38px !important;
  height: 30px;
  justify-content: center;
  padding: 0;
}

.calculatorButton:hover,
.calculatorButton:focus {
  background: #007c6d;
  border-color: #007c6d;
  box-shadow: none;
}

.packagingField {
  flex: 2.5 !important;
}

.lotField {
  position: relative;
}

.lotField label {
  background: #fff;
  color: #666360;
  font-family: Poppins-Regular, sans-serif !important;
  font-size: 9px;
  font-weight: 600;
  left: 5px;
  line-height: 1;
  margin: 0;
  padding: 0 2px;
  position: absolute;
  top: -4px;
  z-index: 1;
}

.lotField .borderInput {
  border: 1px solid #d5d3d3;
  color: #5c5855;
  font-size: 13px;
  height: 30px;
}

.technicalTextAreas > * {
  flex-basis: 50%;
}

.calculatorFields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 12px;
}

.calculatorRow {
  display: flex;
  gap: 12px;
  width: 100%;
}

.calculatorRow > * {
  flex: 1;
}

.fieldWithHint {
  display: flex;
  flex-direction: column;
}

.fieldWithHint small,
.doseMode > span {
  color: #7a9c87;
  font-size: 9px;
  padding-left: 2px;
}

.doseMode {
  display: flex;
  flex-direction: column;
  min-width: 250px;
}

.radio-input {
  border: 1px solid #7a9c87;
  border-radius: 8px;
  display: flex;
  overflow: hidden;
}

.radio-input label {
  cursor: pointer;
  flex: 1;
  font-size: 10px;
  margin: 0;
  text-align: center;
}

.radio-input input {
  display: none;
}

.radio-input span {
  display: block;
  padding: 6px 3px;
}

.radio-input input:checked + span {
  background: #7a9c87;
  color: white;
}

.formActions {
  align-items: center;
  background: #fff;
  box-shadow: 0 -2px 5px 0 rgba(0, 0, 0, 0.197);
  display: flex;
  flex: 0 0 auto;
  justify-content: space-around;
  padding: 10px 0;
  z-index: 10;
}

.primaryButton,
.secondaryButton {
  align-items: center;
  border-radius: 3px;
  display: flex;
  font-size: 12px;
  height: 28px;
  justify-content: center;
  min-width: 125px;
  padding: 0 40px;
}

.primaryButton {
  background: #006256;
  border-color: #006256;
  color: whitesmoke;
}

.secondaryButton {
  background: #eeeeee;
  border-color: #006256;
  color: #006256;
  font-family: Poppins-Regular, sans-serif !important;
  font-weight: 600;
}

.primaryButton:hover,
.primaryButton:focus,
.primaryButton:not(:disabled):active {
  background: #006256;
  border-color: #006256;
  box-shadow: none;
}

.secondaryButton:hover,
.secondaryButton:focus,
.secondaryButton:not(:disabled):active {
  background: #eeeeee;
  border-color: #006256;
  box-shadow: 0 0 0 1px #006256;
  color: #006256;
}

.listSection {
  background: #fff;
  border: 1px dashed #b2b2b2;
  border-radius: 5px;
  box-sizing: border-box;
  display: flex;
  flex: 0 0 clamp(270px, 34vh, 360px);
  flex-direction: column;
  margin: 0 20px 16px;
  min-height: 270px;
  overflow: hidden;
  padding: 10px 14px 8px;
}

.listHeader {
  align-items: center;
  display: flex;
  flex: 0 0 48px;
  gap: 20px;
  justify-content: space-between;
}

.listTitle {
  display: flex;
  flex-direction: column;
  min-width: 155px;
}

.listHeader strong {
  color: #006458;
  font-family: Poppins-Medium, sans-serif !important;
  font-size: 14px;
  font-weight: 600;
}

.itemCount {
  color: #8a8a8a;
  font-size: 9px;
  margin-top: -1px;
}

.searchGroup {
  display: flex;
  max-width: 420px;
  width: 38%;
}

.searchGroup input {
  border-color: #d2ddd9;
  border-radius: 4px 0 0 4px;
  font-size: 11px;
  height: 30px;
  padding-left: 10px;
}

.searchButton {
  background: #006458;
  border-color: #006458;
  border-radius: 0 4px 4px 0;
  height: 30px;
  min-width: 38px;
}

.tableWrapper {
  background: #fff;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 4px 3px;
}

.presetTable {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-height: 100%;
  min-width: 1040px;
  width: 100%;
}

.tableRow {
  align-items: center;
  background: #f8f8f8;
  border: 1px solid rgba(0, 100, 88, 0.35);
  border-radius: 5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: #006257;
  display: grid;
  font-size: 12px;
  grid-template-columns:
    minmax(190px, 1.35fr)
    minmax(110px, 0.75fr)
    minmax(170px, 1.15fr)
    minmax(160px, 1.05fr)
    minmax(82px, 0.5fr)
    minmax(180px, 1.2fr)
    82px;
  min-height: 54px;
  padding: 5px 8px;
  transition: background-color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;
  width: 100%;
}

.tableRow:hover {
  background: #eef6f3;
  box-shadow: 0 2px 5px rgba(0, 98, 86, 0.14);
  transform: translateY(-1px);
}

.cell {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  padding: 3px 10px;
}

.productCell {
  align-items: center;
  flex-direction: row;
  gap: 9px;
}

.cellContent {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.itemNumber {
  align-items: center;
  background: #006458;
  border-radius: 50%;
  color: #fff;
  display: flex;
  flex: 0 0 24px;
  font-family: Poppins-Medium, sans-serif !important;
  font-size: 10px;
  height: 24px;
  justify-content: center;
}

.cellLabel {
  color: #8a8a8a;
  font-size: 8px;
  letter-spacing: 0.35px;
  line-height: 1.2;
  text-transform: uppercase;
}

.cellValue {
  color: #285d56;
  display: block;
  font-family: Poppins-Medium, sans-serif !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quantityCell {
  align-items: center;
  text-align: center;
}

.quantityValue {
  align-items: center;
  background: #e3efeb;
  border-radius: 12px;
  color: #006458;
  display: inline-flex;
  font-family: Poppins-Medium, sans-serif !important;
  height: 22px;
  justify-content: center;
  margin-top: 2px;
  min-width: 34px;
  padding: 0 8px;
}

.actionsCell {
  align-items: center;
  display: flex;
  gap: 7px;
  justify-content: center;
  padding-left: 8px;
}

.actionButton {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 4px;
  display: flex;
  height: 30px;
  justify-content: center;
  transition: background-color 0.2s ease, color 0.2s ease;
  width: 30px;
}

.actionButton:hover {
  color: #fff;
}

.editButton {
  background: #e1efec;
  border-color: #b9d7d1;
  color: #006458;
}

.editButton:hover {
  background: #006458;
}

.deleteButton {
  background: #f3e7e7;
  border-color: #dfc7c7;
  color: #7c2929;
}

.deleteButton:hover {
  background: #7c2929;
}

.listState {
  align-items: center;
  color: #777;
  display: flex;
  flex: 1;
  gap: 8px;
  justify-content: center;
  min-height: 90px;
}

.presetPagination {
  align-items: center;
  background: #fff;
  display: flex;
  flex: 0 0 38px;
  gap: 3px;
  justify-content: center;
  margin: 0;
  min-height: 38px;
  position: relative;
  z-index: 1;
}

.pageButton,
.currentPage {
  align-items: center;
  border: 1px solid #d4e0dc;
  display: flex;
  height: 26px;
  justify-content: center;
  width: 30px;
}

.pageButton {
  background: #f1f5f3;
  color: #006458;
}

.pageButton:first-child {
  border-radius: 4px 0 0 4px;
}

.pageButton:last-child {
  border-radius: 0 4px 4px 0;
}

.pageButton:not(:disabled):hover {
  background: #dfece8;
}

.pageButton:disabled {
  color: #afbbb7;
  cursor: not-allowed;
}

.currentPage {
  background: #006458;
  border-color: #006458;
  color: #fff;
  font-family: Poppins-Medium, sans-serif !important;
  font-size: 11px;
}

@media (max-width: 900px) {
  .listHeader {
    align-items: stretch;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 10px;
  }

  .searchGroup {
    max-width: none;
    width: 100%;
  }
}
</style>
